/**
 * Robot groups for the calendar lanes -- a THIN LOCAL ADAPTER.
 *
 * Per-site editable groups are being built on `robot-groups-20261030`
 * against this contract:
 *
 *   GET /api/v1/sites/{site}/settings/robot-groups
 *     -> { mode: 'default' | 'custom',
 *          groups: [{ id, name, robots: ['<fleet>/<robot>'] }],
 *          robots: [{ key, fleet, name, service, label }],
 *          canEdit: boolean }
 *
 * and `useRobotGroups` below reads it through the shared hook in
 * `src/api/hooks/useRobotGroups.ts`. Everything else -- the fallback, the
 * merge with live robots -- is pure.
 *
 * FALLBACK. An api-server without the route (404/405, `null` here) still
 * gets groups: the DEFAULT grouping by robot type, derived on the client
 * from what it already holds -- the live robot list for who exists, and
 * the categories of the work each robot has done for what type it is
 * (fleet-name keywords as a last resort). `canEdit` is then false: there
 * is nothing to edit.
 */
import { useMemo } from 'react';
import { useRobotGroups as useSharedRobotGroups } from '../../../api/hooks/useRobotGroups';
import type { RobotStateView, TaskState } from '../../../api/rmf-client';
import { categoryOf } from '../calendar-categories';
import { cleanModeOfTask } from '../clean-modes';
import {
  SERVICE_LABEL,
  SERVICE_ORDER,
  serviceOfLane,
  type GroupDef,
  type RobotEntry,
  type RobotService,
} from './lane-layout';
import { robotKeyOf } from './lane-items';

export interface RobotGroupsContract {
  mode: 'default' | 'custom';
  groups: Array<{ id: string; name: string; robots: string[] }>;
  robots: Array<{ key: string; fleet: string; name: string; service: string; label: string }>;
  canEdit: boolean;
}

/** `null` data = this api-server has no such route (use the fallback).
 *  Reads through the shared `useRobotGroups` hook, so the calendar shares
 *  its cache: a save in Site settings shows here at once. */
export function useRobotGroups(siteId: string) {
  const q = useSharedRobotGroups(siteId);
  const data = useMemo(
    () => (q.data === undefined ? undefined : normaliseContract(q.data)),
    [q.data],
  );
  return { ...q, data };
}

/** Defensive read of the wire: a malformed answer is treated as absent
 *  (the fallback) rather than taking the calendar down. */
export function normaliseContract(body: unknown): RobotGroupsContract | null {
  if (!body || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  if (!Array.isArray(o.groups) || !Array.isArray(o.robots)) return null;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    mode: o.mode === 'custom' ? 'custom' : 'default',
    canEdit: o.canEdit === true,
    groups: o.groups
      .filter((g): g is Record<string, unknown> => Boolean(g) && typeof g === 'object')
      .map((g, i) => ({
        id: str(g.id) || `group-${i}`,
        name: str(g.name) || 'Robots',
        robots: Array.isArray(g.robots)
          ? g.robots.filter((k): k is string => typeof k === 'string')
          : [],
      })),
    robots: o.robots
      .filter((r): r is Record<string, unknown> => Boolean(r) && typeof r === 'object')
      .map((r) => ({
        key: str(r.key) || `${str(r.fleet)}/${str(r.name)}`,
        fleet: str(r.fleet),
        name: str(r.name),
        service: str(r.service),
        label: str(r.label) || str(r.name),
      }))
      .filter((r) => r.name !== ''),
  };
}

export function toService(value: string | null | undefined): RobotService {
  const v = (value ?? '').toLowerCase();
  return (SERVICE_ORDER as readonly string[]).includes(v) ? (v as RobotService) : 'other';
}

const FLEET_HINTS: ReadonlyArray<[RegExp, RobotService]> = [
  [/clean|vacuum|scrub|sweep|phantas|gausium|scrubber|mop/i, 'cleaning'],
  [/deliver|butler|bella|tray|serv(e|ing)bot|amenit/i, 'delivery'],
  [/linen|laundry/i, 'linen'],
  [/patrol|security|guard/i, 'patrol'],
];

/**
 * A robot's type from the work it has done: the service most of its
 * (classifiable) tasks belong to. Charges, station trips and anything
 * unclassified say nothing about type. With no such history, the fleet
 * name's keywords; else `other`.
 */
export function deriveRobotService(
  fleet: string,
  name: string,
  laneCounts: ReadonlyMap<RobotService, number> | undefined,
): RobotService {
  let best: RobotService | null = null;
  let bestN = 0;
  for (const s of SERVICE_ORDER) {
    const n = laneCounts?.get(s) ?? 0;
    if (n > bestN) {
      best = s;
      bestN = n;
    }
  }
  if (best) return best;
  for (const [re, service] of FLEET_HINTS) if (re.test(fleet) || re.test(name)) return service;
  return 'other';
}

/** Per robot key, how many of its tasks fall in each service. */
export function serviceHints(tasks: readonly TaskState[]): Map<string, Map<RobotService, number>> {
  const out = new Map<string, Map<RobotService, number>>();
  for (const t of tasks) {
    const key = robotKeyOf(t.assignedFleet, t.assignedRobot);
    if (!key) continue;
    const service = serviceOfLane(categoryOf(t.type, cleanModeOfTask(t)));
    if (!service) continue;
    const counts = out.get(key) ?? new Map<RobotService, number>();
    counts.set(service, (counts.get(service) ?? 0) + 1);
    out.set(key, counts);
  }
  return out;
}

/** The default grouping: one group per robot type, in a fixed order,
 *  empty types left out, robots by name inside each. */
export function defaultGroups(robots: readonly RobotEntry[]): GroupDef[] {
  return SERVICE_ORDER.map((service) => ({
    id: service,
    name: SERVICE_LABEL[service],
    robots: robots
      .filter((r) => r.service === service)
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
      .map((r) => r.key),
  })).filter((g) => g.robots.length > 0);
}

export interface ResolvedGroups {
  robots: RobotEntry[];
  groups: GroupDef[];
  mode: 'default' | 'custom';
  canEdit: boolean;
  /** Where the groups came from: the site's settings, or derived here. */
  source: 'server' | 'derived';
}

/**
 * The robots and groups the lanes draw.
 *
 * `server` undefined = still loading or failed (derive meanwhile, so the
 * lanes never wait on a settings read); `null` = no such route (derive).
 * The live robot list adds the floor each robot is on, and any robot the
 * site's list does not name -- it appears under "Other robots".
 */
export function resolveRobotGroups(args: {
  server: RobotGroupsContract | null | undefined;
  liveRobots: readonly RobotStateView[];
  /** Live tasks, to tell a robot's type when the server cannot. */
  tasks: readonly TaskState[];
}): ResolvedGroups {
  const levels = new Map<string, string>();
  for (const r of args.liveRobots) {
    const key = robotKeyOf(r.fleet, r.name);
    if (key && r.location?.map) levels.set(key, r.location.map);
  }

  if (args.server) {
    const robots: RobotEntry[] = args.server.robots.map((r) => ({
      key: r.key,
      fleet: r.fleet,
      name: r.name,
      label: r.label || r.name,
      service: toService(r.service),
      level: levels.get(r.key),
    }));
    const known = new Set(robots.map((r) => r.key));
    const hints = serviceHints(args.tasks);
    for (const r of args.liveRobots) {
      const key = robotKeyOf(r.fleet, r.name);
      if (!key || known.has(key)) continue;
      known.add(key);
      robots.push({
        key,
        fleet: r.fleet,
        name: r.name,
        label: r.name,
        service: deriveRobotService(r.fleet, r.name, hints.get(key)),
        level: levels.get(key),
      });
    }
    return {
      robots,
      groups: args.server.groups.map((g) => ({ id: g.id, name: g.name, robots: g.robots })),
      mode: args.server.mode,
      canEdit: args.server.canEdit,
      source: 'server',
    };
  }

  const hints = serviceHints(args.tasks);
  const robots: RobotEntry[] = [];
  const seen = new Set<string>();
  for (const r of args.liveRobots) {
    const key = robotKeyOf(r.fleet, r.name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    robots.push({
      key,
      fleet: r.fleet,
      name: r.name,
      label: r.name,
      service: deriveRobotService(r.fleet, r.name, hints.get(key)),
      level: levels.get(key),
    });
  }
  return {
    robots,
    groups: defaultGroups(robots),
    mode: 'default',
    canEdit: false,
    source: 'derived',
  };
}

const LINEN_GROUP = /\blinen\b/i;

/**
 * The robots whose deliveries are drawn as linen runs (pink "Ln"): every
 * robot whose type is Linen, and every robot in a group whose name says
 * Linen. The group rule is what makes it follow the site's editable
 * groups -- move a robot into the Linen group and its runs turn pink;
 * in the default grouping the Linen group IS the Linen type.
 */
export function linenRobotKeys(
  robots: readonly Pick<RobotEntry, 'key' | 'service'>[],
  groups: readonly Pick<GroupDef, 'name' | 'robots'>[],
): Set<string> {
  const out = new Set<string>();
  for (const r of robots) if (r.service === 'linen') out.add(r.key);
  for (const g of groups) if (LINEN_GROUP.test(g.name)) for (const k of g.robots) out.add(k);
  return out;
}
